import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  addOptionalBoolField,
  addOptionalField,
  addOptionalFreeField,
  addOptionalOnOffField,
} from '@utils/advancedSettings';
import {
  ADVANCED_SETTING_VALUE_TYPES,
  getAdvancedSettingsDefinitions,
  getToolAdvancedSettingFields,
} from '@constants/advancedSettings';
import { logError } from '@utils/usefulFuncs';
import { advancedSettingsActionTypes } from '@constants/actionTypes';
import { loadAdvancedSettingsForMode } from '@services/advancedSettings';

export function addAdvancedSettingValues(values, form, skipValues = []) {
  const neverIncludeValues = ['enableDailySummaries', 'overrideQuestion', ...skipValues];
  const entries = Object.entries(values);
  entries.forEach(([keyname, value]) => {
    const shouldSkip = neverIncludeValues.includes(keyname);
    const metadata = getAdvancedSettingAPIMetadata(keyname);
    const keyAlreadySet = metadata && Object.prototype.hasOwnProperty.call(form, metadata.keyName);

    if (shouldSkip || !metadata || keyAlreadySet) {
      // do nothing
    } else {
      const { keyName, valueType } = metadata;
      switch (valueType) {
        case ADVANCED_SETTING_VALUE_TYPES.custom: {
          addOptionalFreeField(keyName, value, form);
          break;
        }
        case ADVANCED_SETTING_VALUE_TYPES.date: {
          addOptionalFreeField(keyName, value, form);
          break;
        }
        case ADVANCED_SETTING_VALUE_TYPES.string: {
          addOptionalFreeField(keyName, value, form);
          break;
        }
        case ADVANCED_SETTING_VALUE_TYPES.integer: {
          addOptionalField(keyName, value, form);
          break;
        }
        case ADVANCED_SETTING_VALUE_TYPES.float: {
          addOptionalField(keyName, value, form, true);
          break;
        }
        case ADVANCED_SETTING_VALUE_TYPES.bool: {
          addOptionalBoolField(keyName, value, form);
          break;
        }
        case ADVANCED_SETTING_VALUE_TYPES.onOff: {
          addOptionalOnOffField(keyName, value, form);
          break;
        }
        default:
          logError('Unknown advanced setting value type for advanced setting', {
            fieldName: keyname,
            apiKeyName: keyName,
            valueType,
            value,
          });
          break;
      }
    }
  });

  return form;
}

export function generateBoolLabels() {
  const allSettings = getAdvancedSettingsDefinitions();

  const boolLabels = [];
  Object.values(allSettings).forEach((setting) => {
    if (setting.valueType === ADVANCED_SETTING_VALUE_TYPES.bool) {
      boolLabels.push(setting.keyName);
    }
  });
  return boolLabels;
}

export function generateOnOffLabels() {
  const allSettings = getAdvancedSettingsDefinitions();

  const onOffLabels = [];
  Object.values(allSettings).forEach((setting) => {
    if (setting.valueType === ADVANCED_SETTING_VALUE_TYPES.onOff) {
      onOffLabels.push(setting.keyName);
    }
  });
  return onOffLabels;
}

export const useAdvancedSettingsToolFields = (tool) => {
  return getToolAdvancedSettingFields(tool);
};

export const useAdvancedSettingsInitialization = (tool) => {
  const dispatch = useDispatch();

  React.useEffect(() => {
    if (!tool) return undefined;
    const cancel = { isCanceled: false };
    dispatch(loadAdvancedSettingsForMode(tool));
    return () => {
      cancel.isCanceled = true;
    };
  }, [tool]);
};

export function getAdvancedSettingAPIMetadata(name) {
  return getAdvancedSettingsDefinitions()[name] || null;
}

export function getAdvancedSettingDefaultValue(name) {
  const metadata = getAdvancedSettingAPIMetadata(name);
  return metadata?.defaultValue || null;
}

export function getAdvancedSettingType(name) {
  const metadata = getAdvancedSettingAPIMetadata(name);
  return metadata?.valueType || null;
}

export function toAdvancedSettingDisplayName(name) {
  const metadata = getAdvancedSettingAPIMetadata(name);
  return metadata?.formDisplayName || null;
}

export function getAdvancedSettingDisplayName(name) {
  const metadata = getAdvancedSettingAPIMetadata(name);
  return metadata?.formDisplayName || null;
}

export function getAdvancedSettingKeyName(name) {
  const metadata = getAdvancedSettingAPIMetadata(name);
  return metadata?.keyName || null;
}

export function useAdvancedSettingsFields() {
  return useSelector((state) => state.advancedSettings.advancedSettingsFields);
}

export function useShowAdvancedSettings(tool) {
  const dispatch = useDispatch();

  const action = React.useCallback(async () => {
    await dispatch(loadAdvancedSettingsForMode(tool));
    dispatch({ type: advancedSettingsActionTypes.showAdvancedSettings, payload: tool });
  }, [tool]);

  return action;
}

export function useAdvancedSettingsLoading() {
  return useSelector((state) => state.advancedSettings.loadingMode !== null);
}

export function useShouldShowAdvancedSettings() {
  const fields = useAdvancedSettingsFields();
  return fields?.length > 0;
}

export function useAdvancedSettings() {
  const state = useSelector((state) => state.advancedSettings.advancedSettingsValues);
  return state;
}
